// eslint-disable-next-line spire/export-chain
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import Zone from "@insite/client-framework/Components/Zone";
import Logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryDepthLoaded,
    getCategoryState,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";

import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { MappedLink } from "@tIntermatic/Overrides/Widgets/Header/MainNavigation";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    cssClass = "cssClass",
}

interface OwnProps extends PageProps {
    fields: {
        links: MappedLink[];
        [fields.cssClass]: string;
    };
}
interface LinkModel {
    fields: {
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        linkType: "Link" | "MegaMenu" | "CascadingMenu";
        overrideTitle: string;
        title: string;
        numberOfColumns: number;
        maxDepth: number;
        isClickable: boolean;
        shopAllProduct?: boolean;
        CustomMenu?: boolean;
    };
}

export interface SiteMapPageStyle {
    cutomPageContainer?: InjectableCss;
    container?: InjectableCss;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryNameLinkItem?: GridItemProps;
    categoryName?: TypographyProps;
    title?: InjectableCss;
    siteMapGrid?: InjectableCss;
    titleGrid?: InjectableCss;
    linkContainer?: InjectableCss;
    categoryWrapper?: InjectableCss;
    categoryLinks?: InjectableCss;
    subCategoryName?: TypographyProps;
    subCategoryList?: TypographyProps;
    categoryGridWrapper?: InjectableCss;
}

export const styles: SiteMapPageStyle = {
    cutomPageContainer: {
        css: css`
            max-width: 100% !important;
            padding: 0 !important;
            padding-bottom: 50px !important;
        `,
    },
    container: {
        css: css`
            margin: 0 auto;
            div[class^="GridOffset"] {
                padding-left: 138px;
                padding-right: 138px;
                margin: 0 auto;
                @media all and (max-width: 991px) {
                    max-width: 100%;
                    padding: 0 30px;
                }
            }
        `,
    },

    titleGrid: {
        css: css`
            width: 100%;
        `,
    },
    siteMapGrid: {
        css: css`
            display: flex;
            width: 100%;
            flex-wrap: wrap;
        `,
    },
    title: {
        css: css`
            color: #33333a;
            font-family: "proxima-nova";
            font-size: 50px;
            font-weight: 300;
            letter-spacing: 0;
            line-height: 61px;
            padding-top: 60px;
            padding-bottom: 56px;
        `,
    },
    linkContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 30px;
        `,
    },
    categoryName: {
        css: css`
            color: #006bc0;
            font-size: 14px;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 24px;
            text-transform: uppercase;
            font-family: proxima-nova;
        `,
    },
    subCategoryName: {
        css: css`
            color: #006bc0;
            font-size: 12px;
            letter-spacing: 0;
            font-weight: bold;
            line-height: 26px;
            text-transform: uppercase;
            padding-left: 30px;
            font-family: proxima-nova;
            @media all and (max-width: 768px) {
                padding-left: 0;
            }
            > span {
                color: #006bc0 !important;
            }
        `,
    },
    subCategoryList: {
        css: css`
            color: #006bc0;
            font-size: 12px;
            letter-spacing: 0;
            line-height: 26px;
            text-transform: uppercase;
            padding-left: 30px;
            font-family: proxima-nova;
            @media all and (max-width: 768px) {
                padding-left: 0;
            }
        `,
    },
    categoryGridWrapper: {
        css: css`
            width: 33%;
            @media all and (max-width: 991px) {
                width: 50%;
            }
            @media all and (max-width: 412px) {
                width: 100%;
            }
        `,
    },
    categoryLinks: {
        css: css`
            padding-bottom: 10px;
        `,
    },
};

const mapStateToProps = (state: ApplicationState) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/MainNavigation" && widget.zone === "MobileNAv";
        })?.fields.links as LinkModel[]) ?? [];
    const mappedLinks = [];
    const categoryIdsToLoad: Dictionary<number> = {};
    for (const link of headerLinkListLinkFields) {
        const {
            fields: {
                linkType,
                destination,
                openInNewWindow,
                overrideTitle,
                numberOfColumns,
                maxDepth,
                title,
                isClickable,
                shopAllProduct,
                CustomMenu,
            },
        } = link;
        const { type, value } = destination;

        const depthToLoad = maxDepth;
        let mappedLink: MappedLink | undefined;

        if (type === "Url") {
            mappedLink = {
                url: value,
                title,
            };
        } else if (type === "Page") {
            const readonlyPageLink = getPageLinkByNodeId(state, value);
            if (readonlyPageLink) {
                mappedLink = { ...readonlyPageLink };
                if (overrideTitle) {
                    mappedLink.title = overrideTitle;
                }
            }
        } else if (type === "Category") {
            mappedLink = setupCategoryLink(state, value, categoryIdsToLoad, depthToLoad, overrideTitle);
        }

        if (!mappedLink) {
            continue;
        }

        mappedLink.openInNewWindow = openInNewWindow;
        mappedLink.isClickable = isClickable;
        mappedLink.numberOfColumns = numberOfColumns;
        mappedLink.maxDepth = maxDepth;
        mappedLink.shopAllProduct = shopAllProduct;
        mappedLink.CustomMenu = CustomMenu;

        if (linkType === "MegaMenu" || linkType === "CascadingMenu") {
            mappedLink.childrenType = linkType;
        }

        mappedLinks.push(mappedLink);
    }
    return {
        mappedLinks,
        categoryIdsToLoad,
        categoryErrorStatusCodeById: state.data.categories.errorStatusCodeById,
    };
};
const getParameter = (categoryId: string, maxDepth: number) => ({
    maxDepth,
    startCategoryId: categoryId === emptyGuid ? undefined : categoryId,
    includeStartCategory: categoryId !== emptyGuid,
});
const setupCategoryLink = (
    state: ApplicationState,
    value: string,
    categoryIdsToLoad: Dictionary<number>,
    maxDepth: number,
    overrideTitle: string,
) => {
    let mappedLink: MappedLink | undefined;
    if (!value) {
        return;
    }

    const depthLoaded = getCategoryDepthLoaded(state, value);
    const subCategoryIds = state.data.categories.parentCategoryIdToChildrenIds[value];
    if (
        (depthLoaded < maxDepth || !subCategoryIds) &&
        !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
    ) {
        categoryIdsToLoad[value] = maxDepth;
    } else {
        if (value === emptyGuid) {
            mappedLink = {
                url: "",
                title: overrideTitle || translate("Products"),
            };
        } else {
            const categoryState = getCategoryState(state, value);
            if (!categoryState.value) {
                if (!categoryState.isLoading) {
                    categoryIdsToLoad[value] = maxDepth;
                }
                return;
            }
            mappedLink = {
                url: categoryState.value.path,
                title: overrideTitle || categoryState.value.shortDescription,
            };
        }

        loadSubCategories(mappedLink, subCategoryIds, 1, maxDepth, state);
    }

    return mappedLink;
};
const loadSubCategories = (
    mappedLink: MappedLink,
    subCategoryIds: readonly string[] | undefined,
    currentDepth: number,
    maxDepth: number,
    state: ApplicationState,
) => {
    if (currentDepth > maxDepth) {
        return;
    }
    mappedLink.children = [];
    if (!subCategoryIds) {
        return;
    }
    for (const subCategoryId of subCategoryIds) {
        const subCategory = getCategoryState(state, subCategoryId).value;
        if (!subCategory) {
            Logger.error(`Category ${subCategoryId} was not found in data state, excluding from navigation.`);
            continue;
        }
        const childLink: MappedLink = {
            url: subCategory.path,
            title: subCategory.shortDescription,
            children: [],
        };
        mappedLink.children.push(childLink);

        loadSubCategories(childLink, subCategory.subCategoryIds, currentDepth + 1, maxDepth, state);
    }
};
type Props = HasHistory & OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
const mapDispatchToProps = {
    loadCategories,
};
const INTSiteMapPage = (props: Props) => {
    const {
        fields: { cssClass },
        id,
        mappedLinks,
        categoryIdsToLoad,
        categoryErrorStatusCodeById,
    } = props;

    for (const categoryId of Object.keys(categoryIdsToLoad)) {
        if (!categoryErrorStatusCodeById?.[categoryId]) {
            const maxDepth = categoryIdsToLoad[categoryId];
            loadCategories(getParameter(categoryId, maxDepth));
        }
    }

    return (
        <Page {...styles.cutomPageContainer} className={cssClass}>
            <GridContainer {...styles.container}>
                <StyledWrapper {...styles.titleGrid}>
                    <StyledWrapper {...styles.title}>Sitemap</StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.siteMapGrid}>
                    {mappedLinks.map(link => (
                        <StyledWrapper key={link.title.toString()} {...styles.categoryGridWrapper}>
                            <StyledWrapper {...styles.linkContainer}>
                                <StyledWrapper {...styles.categoryLinks}>
                                    {(link.url || link.title === "Products") && (
                                        <Link href={link.url}>
                                            <Typography {...styles.categoryName}>{link.title}</Typography>
                                        </Link>
                                    )}
                                </StyledWrapper>
                                <StyledWrapper {...styles.categoryLinks}>
                                    {link.children &&
                                        link.children.map(sublink => (
                                            <StyledWrapper key={sublink.title} {...styles.categoryLinks}>
                                                {sublink.url && (
                                                    <Link href={sublink.url} {...styles.subCategoryName}>
                                                        {sublink.title}
                                                    </Link>
                                                )}
                                                {sublink.children &&
                                                    sublink.children.map(sublinkInner => (
                                                        <StyledWrapper key={sublinkInner.title}>
                                                            {sublinkInner.url && (
                                                                <Link
                                                                    href={sublinkInner.url}
                                                                    {...styles.subCategoryList}
                                                                >
                                                                    {sublinkInner.title}
                                                                </Link>
                                                            )}
                                                        </StyledWrapper>
                                                    ))}
                                            </StyledWrapper>
                                        ))}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                    <Zone contentId={id} zoneName="Content1" />
                </StyledWrapper>
            </GridContainer>
        </Page>
    );
};

const pageModule: PageModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(INTSiteMapPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        fieldDefinitions: [
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
        pageType: "Content",
    },
};

export default pageModule;
